// =============================================================================
// Billing & Subscription API Functions
// =============================================================================
// Employer-facing: subscription, invoices, bank details
// Admin-facing: plans, org subscriptions, invoices, revenue, bank accounts, receipts

import { api } from './api'

// ── Shared Types ────────────────────────────────────────────────────────

export type SubscriptionStatus = 'trial' | 'active' | 'past_due' | 'cancelled' | 'suspended' | 'complimentary'
export type InvoiceStatus = 'pending' | 'paid' | 'overdue' | 'cancelled' | 'refunded'
export type ComplimentaryType = 'internal' | 'beta_pilot' | 'strategic_partner' | 'non_profit' | 'goodwill' | 'other'

// ── Employer: Subscription ──────────────────────────────────────────────

export interface EmployerSubscription {
  org_sub_id: number
  org_id: number
  status: SubscriptionStatus
  plan_name: string
  tier: 'growth' | 'enterprise' | null
  billing_cycle: 'monthly' | 'annual' | null
  plan_price_per_user_cents: number
  plan_minimum_monthly_cents: number
  override_price_per_user_cents: number | null
  override_minimum_monthly_cents: number | null
  price_per_user_cents: number
  minimum_monthly_cents: number
  currency: string
  currency_symbol: string | null
  country_name: string | null
  current_period_start: string
  current_period_end: string
  trial_end_date: string | null
  trial_days_remaining: number | null
  active_users: number
  estimated_monthly: number
  complimentary_type: ComplimentaryType | null
  complimentary_expires_at: string | null
  complimentary_notes: string | null
  suspended: boolean
  next_tier_plan_price_per_user_cents: number | null
  pending_change?: {
    invoice_id: number
    plan_name: string
    tier: 'growth' | 'enterprise' | null
    billing_cycle: 'monthly' | 'annual' | null
    total_cents: number
    amount_due_cents: number
    currency: string
    status: InvoiceStatus
  } | null
}

export interface EmployerSubscriptionResponse {
  subscription: EmployerSubscription | null
  setup_required?: boolean
}

/**
 * Get the current organization subscription details.
 * GET /api/v1/organizations/subscription
 */
export async function getMySubscriptionApi(): Promise<EmployerSubscriptionResponse> {
  const res = await api.get('/api/v1/organizations/subscription')
  return res.data
}

export interface UsageLogEntry {
  snapshot_date: string
  active_user_count: number
}

export interface UsageResponse {
  current_active_users: number
  usage_history: UsageLogEntry[]
}

/**
 * Get billing period usage (active users).
 * GET /api/v1/organizations/subscription/usage
 */
export async function getMyUsageApi(): Promise<UsageResponse> {
  const res = await api.get('/api/v1/organizations/subscription/usage')
  return res.data
}

// ── Employer: Invoices ─────────────────────────────────────────────────

export interface EmployerInvoice {
  invoice_id: number
  invoice_number: string
  period_start: string
  period_end: string
  total_active_users: number
  total_cents: number
  amount_due_cents: number
  currency: string
  currency_symbol: string | null
  status: InvoiceStatus
  due_date: string
  paid_at: string | null
  created_at: string
  total_amount: string
}

export interface EmployerInvoicesResponse {
  total: number
  page: number
  limit: number
  data: EmployerInvoice[]
}

/**
 * List invoices for the authenticated user's organization.
 * GET /api/v1/organizations/invoices
 */
export async function getMyInvoicesApi(params?: {
  status?: string
  page?: number
  limit?: number
}): Promise<EmployerInvoicesResponse> {
  const res = await api.get('/api/v1/organizations/invoices', { params })
  return res.data
}

export interface InvoiceLineItem {
  item_id: number
  invoice_id: number
  item_type: string
  description: string
  quantity: number | null
  unit_price_cents: number | null
  amount_cents: number
  tax_rate_percent: string | null
  sort_order: number
}

export interface PaymentReceipt {
  receipt_id: number
  invoice_id: number
  org_id: number
  amount_cents: number
  currency: string
  reference_number: string | null
  sender_account_suffix: string | null
  received_date: string
  matched_by: number | null
  notes: string | null
}

export interface InvoiceBankDetails {
  bank_name: string
  account_holder: string
  account_number: string | null
  iban: string | null
  swift_bic: string | null
}

// F4 — legally valid B2B invoice identity blocks (buyer snapshot + seller profile)
export interface InvoiceBuyer {
  legal_name: string
  vat_number: string | null
  registration_number: string | null
  registered_address: string | null
  registration_country: string | null
}

export interface InvoiceSeller {
  seller_id: number
  legal_name: string
  registration_number: string | null
  vat_number: string | null
  registered_address: string | null
  registration_country: string | null
  contact_email: string | null
}

export interface InvoiceDetail {
  invoice: {
    invoice_id: number
    org_id: number
    org_name: string
    invoice_number: string
    period_start: string
    period_end: string
    total_active_users: number
    total_cents: number
    amount_due_cents: number
    subtotal_cents: number
    currency: string
    currency_symbol: string | null
    status: InvoiceStatus
    due_date: string
    paid_at: string | null
    paid_amount_cents: number | null
    notes: string | null
    created_at: string
    total_amount: string
    total_amount_display: string
    subtotal_amount: string
    buyer?: InvoiceBuyer
    seller?: InvoiceSeller | null
  }
  line_items: InvoiceLineItem[]
  payment_receipts: PaymentReceipt[]
  bank_details: InvoiceBankDetails | null
}

/**
 * Get invoice detail with line items and payment instructions.
 * GET /api/v1/organizations/invoices/:id
 */
export async function getMyInvoiceDetailApi(invoiceId: number): Promise<InvoiceDetail> {
  const res = await api.get(`/api/v1/organizations/invoices/${invoiceId}`)
  return res.data
}

/**
 * Download an invoice as a PDF (F4 legal invoice rendering).
 * GET /api/v1/organizations/invoices/:id/pdf
 */
export async function downloadInvoicePdfApi(invoiceId: number, opts?: { admin?: boolean }): Promise<void> {
  const url = opts?.admin
    ? `/api/v1/admin/invoices/${invoiceId}/pdf`
    : `/api/v1/organizations/invoices/${invoiceId}/pdf`
  const res = await api.get(url, { responseType: 'blob' })
  const blob = res.data as Blob
  const disposition = String(res.headers?.['content-disposition'] || '')
  const match = disposition.match(/filename="?([^";]+)"?/i)
  const filename = match?.[1] || `invoice-${invoiceId}.pdf`
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Defer revoke — revoking synchronously after click() can abort the
  // download in some browsers (e.g. Firefox).
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
}

// ── F4: Seller Profile (PeerPass invoice issuer identity, super-admin) ────

export interface SellerProfile {
  seller_id: number
  legal_name: string
  registration_number: string | null
  vat_number: string | null
  duns_number: string | null
  registered_address: string | null
  registration_country: string | null
  contact_email: string | null
}

/**
 * Get the active seller profile (PeerPass legal entity shown on invoices).
 * GET /api/v1/admin/seller-profile
 */
export async function getSellerProfileApi(): Promise<{ seller: SellerProfile | null }> {
  const res = await api.get('/api/v1/admin/seller-profile')
  return res.data
}

/**
 * Upsert the active seller profile (super-admin).
 * PUT /api/v1/admin/seller-profile
 */
export async function updateSellerProfileApi(params: {
  legal_entity_name: string
  registration_number?: string | null
  vat_number?: string | null
  duns_number?: string | null
  registered_address?: string | null
  registration_country?: string | null
  contact_email?: string | null
}): Promise<{ message: string }> {
  const res = await api.put('/api/v1/admin/seller-profile', params)
  return res.data
}

export interface EmployerBankDetails {
  bank_name: string
  account_holder: string
  account_number: string | null
  account_number_masked: string | null
  iban: string | null
  iban_masked: string | null
  swift_bic: string | null
  currency: string
  notes: string | null
}

/**
 * Get PeerPass's central bank details for transfer.
 * GET /api/v1/organizations/billing/bank-details
 */
export async function getMyBankDetailsApi(): Promise<EmployerBankDetails> {
  const res = await api.get('/api/v1/organizations/billing/bank-details')
  return res.data
}

// ── Admin: Subscription Plans ─────────────────────────────────────────

export interface SubscriptionPlan {
  plan_id: number
  plan_name: string
  country_id: number
  country_name: string
  price_per_user_cents: number
  minimum_monthly_cents: number
  currency: string
  currency_symbol: string | null
  billing_cycle: string
  features: Record<string, unknown> | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface SubscriptionPlansResponse {
  data: SubscriptionPlan[]
}

/**
 * List all subscription plans (admin).
 * GET /api/v1/admin/subscription-plans
 */
export async function getSubscriptionPlansApi(params?: {
  country_id?: number
  is_active?: string
}): Promise<SubscriptionPlansResponse> {
  const res = await api.get('/api/v1/admin/subscription-plans', { params })
  return res.data
}

/**
 * Create a new subscription plan (admin).
 * POST /api/v1/admin/subscription-plans
 */
export async function createSubscriptionPlanApi(params: {
  plan_name: string
  country_id: number
  price_per_user_cents: number
  minimum_monthly_cents?: number
  currency: string
  currency_symbol?: string
  features?: Record<string, unknown>
  is_active?: boolean
}): Promise<{ message: string; plan_id: number }> {
  const res = await api.post('/api/v1/admin/subscription-plans', params)
  return res.data
}

/**
 * Update a subscription plan (admin).
 * PUT /api/v1/admin/subscription-plans/:id
 */
export async function updateSubscriptionPlanApi(
  planId: number,
  params: {
    plan_name?: string
    price_per_user_cents?: number
    minimum_monthly_cents?: number
    currency?: string
    currency_symbol?: string
    features?: Record<string, unknown>
    is_active?: boolean
  },
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/admin/subscription-plans/${planId}`, params)
  return res.data
}

/**
 * Soft-deactivate a subscription plan (admin).
 * DELETE /api/v1/admin/subscription-plans/:id
 */
export async function deleteSubscriptionPlanApi(planId: number): Promise<{ message: string }> {
  const res = await api.delete(`/api/v1/admin/subscription-plans/${planId}`)
  return res.data
}

/**
 * Get plan for a specific country (admin).
 * GET /api/v1/admin/subscription-plans/country/:countryId
 */
export async function getPlanByCountryApi(countryId: number): Promise<{ data: SubscriptionPlan }> {
  const res = await api.get(`/api/v1/admin/subscription-plans/country/${countryId}`)
  return res.data
}

// ── Admin: Organization Subscriptions ──────────────────────────────────

export interface AdminOrgSubscription {
  org_sub_id: number
  org_id: number
  plan_id: number
  plan_id_ref: number
  plan_name: string
  plan_price_per_user_cents: number
  plan_minimum_monthly_cents: number
  override_price_per_user_cents: number | null
  override_minimum_monthly_cents: number | null
  price_per_user_cents: number
  minimum_monthly_cents: number
  currency: string
  currency_symbol: string | null
  status: SubscriptionStatus
  current_period_start: string
  current_period_end: string
  trial_end_date: string | null
  country_name: string | null
  org_name: string
  total_users: number
  set_by_email: string | null
  complimentary_type: ComplimentaryType | null
  complimentary_expires_at: string | null
  complimentary_notes: string | null
  suspension_reason: string | null
  cancelled_at: string | null
  created_at: string
  updated_at: string
}

/**
 * View an organization's subscription (admin).
 * GET /api/v1/admin/organizations/:id/subscription
 */
export async function getOrgSubscriptionApi(orgId: number): Promise<{ data: AdminOrgSubscription }> {
  const res = await api.get(`/api/v1/admin/organizations/${orgId}/subscription`)
  return res.data
}

/**
 * Update an organization's subscription (admin).
 * PUT /api/v1/admin/organizations/:id/subscription
 */
export async function updateOrgSubscriptionApi(
  orgId: number,
  params: {
    plan_id?: number
    status?: SubscriptionStatus
    suspension_reason?: string
    override_price_per_user_cents?: number | null
    override_minimum_monthly_cents?: number | null
  },
): Promise<{ message: string }> {
  const res = await api.put(`/api/v1/admin/organizations/${orgId}/subscription`, params)
  return res.data
}

/**
 * Activate or re-activate an org's subscription (admin).
 * POST /api/v1/admin/organizations/:id/subscription/activate
 */
export async function activateOrgSubscriptionApi(orgId: number): Promise<{ message: string }> {
  const res = await api.post(`/api/v1/admin/organizations/${orgId}/subscription/activate`)
  return res.data
}

/**
 * Cancel an org's subscription (admin).
 * POST /api/v1/admin/organizations/:id/subscription/cancel
 */
export async function cancelOrgSubscriptionApi(orgId: number): Promise<{ message: string }> {
  const res = await api.post(`/api/v1/admin/organizations/${orgId}/subscription/cancel`)
  return res.data
}

/**
 * Set an org to complimentary (admin).
 * PUT /api/v1/admin/organizations/:id/subscription/complimentary
 */
export async function setComplimentaryApi(
  orgId: number,
  params: {
    complimentary_type: ComplimentaryType
    complimentary_notes?: string
    complimentary_expires_at?: string
  },
): Promise<{ message: string }> {
  const res = await api.put(
    `/api/v1/admin/organizations/${orgId}/subscription/complimentary`,
    params,
  )
  return res.data
}

/**
 * Remove complimentary status from an org (admin).
 * PUT /api/v1/admin/organizations/:id/subscription/remove-complimentary
 */
export async function removeComplimentaryApi(orgId: number): Promise<{ message: string }> {
  const res = await api.put(
    `/api/v1/admin/organizations/${orgId}/subscription/remove-complimentary`,
  )
  return res.data
}

/**
 * Employer-facing: change the organization's subscription tier.
 * POST /api/v1/organizations/subscription/change-tier
 */
export async function changeSubscriptionTierApi(
  tier: 'growth' | 'enterprise',
  billing_cycle?: 'monthly' | 'annual',
): Promise<{
  message: string
  invoice_id: number
  plan: {
    plan_id: number
    plan_name: string
    tier: string
    billing_cycle: string
    price_per_user_cents: number
    minimum_monthly_cents: number
  }
}> {
  const res = await api.post('/api/v1/organizations/subscription/change-tier', { tier, billing_cycle })
  return res.data
}

// ── Admin: Invoices ────────────────────────────────────────────────────

export interface AdminInvoice {
  invoice_id: number
  org_id: number
  org_name: string
  invoice_number: string
  period_start: string
  period_end: string
  total_active_users: number
  total_cents: number
  amount_due_cents: number
  currency: string
  currency_symbol: string | null
  status: InvoiceStatus
  due_date: string
  paid_at: string | null
  created_at: string
  total_amount: string
}

export interface AdminInvoicesResponse {
  total: number
  page: number
  limit: number
  data: AdminInvoice[]
}

/**
 * List all invoices across all orgs (admin).
 * GET /api/v1/admin/invoices
 */
export async function getAdminInvoicesApi(params?: {
  org_id?: number
  status?: string
  from_date?: string
  to_date?: string
  page?: number
  limit?: number
}): Promise<AdminInvoicesResponse> {
  const res = await api.get('/api/v1/admin/invoices', { params })
  return res.data
}

/**
 * Get invoice detail (admin).
 * GET /api/v1/admin/invoices/:id
 */
export async function getAdminInvoiceDetailApi(invoiceId: number): Promise<InvoiceDetail> {
  const res = await api.get(`/api/v1/admin/invoices/${invoiceId}`)
  return res.data
}

/**
 * Manually mark an invoice as paid (admin).
 * POST /api/v1/admin/invoices/:id/mark-paid
 */
export async function markInvoicePaidApi(
  invoiceId: number,
  params: {
    amount_cents?: number
    received_date: string
    reference_number?: string
    notes?: string
  },
): Promise<{ message: string }> {
  const res = await api.post(`/api/v1/admin/invoices/${invoiceId}/mark-paid`, params)
  return res.data
}

/**
 * Trigger manual invoice generation (admin).
 * POST /api/v1/admin/invoices/generate
 */
export async function generateInvoicesApi(): Promise<{
  message?: string
  period_start?: string
  period_end?: string
  total?: number
  results?: unknown[]
}> {
  const res = await api.post('/api/v1/admin/invoices/generate')
  return res.data
}

// ── Admin: Revenue Dashboard ──────────────────────────────────────────

export interface RevenueMetrics {
  mrr: string
  mrr_cents: number
  arr: string
  arr_cents: number
  outstanding_ar: string
  outstanding_ar_cents: number
  unpaid_invoices: number
  active_subscriptions: number
  trial_orgs: number
  suspended_orgs: number
  complimentary_orgs: number
  avg_revenue_per_org: string
}

export interface RevenueByCountry {
  country: string
  iso_3: string | null
  currency: string
  invoice_count: number
  revenue_cents: number
  revenue: string
}

export interface RevenueTrend {
  month: string
  invoice_count: number
  revenue_cents: number
  revenue: string
}

export interface RevenueDashboardResponse {
  metrics: RevenueMetrics
  revenue_by_country: RevenueByCountry[]
  monthly_trend: RevenueTrend[]
}

/**
 * Get revenue dashboard metrics (admin).
 * GET /api/v1/admin/revenue/dashboard
 */
export async function getRevenueDashboardApi(): Promise<RevenueDashboardResponse> {
  const res = await api.get('/api/v1/admin/revenue/dashboard')
  return res.data
}

// ── Admin: Bank Account ────────────────────────────────────────────────

export interface AdminBankAccount {
  bank_account_id: number
  bank_name: string
  account_holder: string
  account_number_masked: string | null
  routing_number_masked: string | null
  swift_bic: string | null
  iban_masked: string | null
  currency: string
  is_primary: boolean
  notes: string | null
}

/**
 * Get PeerPass's central bank account (masked) (admin).
 * GET /api/v1/admin/billing/bank-account
 */
export async function getBankAccountApi(): Promise<AdminBankAccount> {
  const res = await api.get('/api/v1/admin/billing/bank-account')
  return res.data
}

/**
 * Update the central bank account details (admin).
 * PUT /api/v1/admin/billing/bank-account
 */
export async function updateBankAccountApi(params: {
  bank_name: string
  account_holder: string
  account_number?: string
  routing_number?: string
  swift_bic?: string
  iban?: string
  currency?: string
  notes?: string
}): Promise<{ message: string }> {
  const res = await api.put('/api/v1/admin/billing/bank-account', params)
  return res.data
}

// ── Admin: Payment Receipts ────────────────────────────────────────────

export interface AdminReceipt {
  receipt_id: number
  invoice_id: number
  org_id: number
  amount_cents: number
  currency: string
  reference_number: string | null
  received_date: string
  matched_by: number | null
  matched_by_email: string | null
  notes: string | null
  org_name: string
  amount_display: string
}

export interface ReceiptsResponse {
  total: number
  page: number
  limit: number
  data: AdminReceipt[]
}

/**
 * List all payment receipts (admin).
 * GET /api/v1/admin/receipts
 */
export async function getReceiptsApi(params?: {
  org_id?: number
  invoice_id?: number
  page?: number
  limit?: number
}): Promise<ReceiptsResponse> {
  const res = await api.get('/api/v1/admin/receipts', { params })
  return res.data
}

/**
 * List unmatched transfers (admin).
 * GET /api/v1/admin/receipts/unmatched
 */
export async function getUnmatchedTransfersApi(): Promise<{ message: string; data: unknown[] }> {
  const res = await api.get('/api/v1/admin/receipts/unmatched')
  return res.data
}

// ── Stripe Payment ────────────────────────────────────────────────────

/**
 * Create a Stripe Checkout Session for an invoice.
 * Returns a redirect URL the user follows to complete card payment.
 * POST /api/v1/organizations/invoices/:id/pay
 */
export async function payInvoiceApi(invoiceId: number): Promise<{
  url: string
  session_id: string
}> {
  const res = await api.post(`/api/v1/organizations/invoices/${invoiceId}/pay`)
  return res.data
}

/**
 * Get the Stripe publishable key for the frontend.
 * Used to initialize Stripe.js if needed for inline checkout.
 * GET /api/v1/config/stripe-publishable-key
 */
export async function getStripePublishableKeyApi(): Promise<{
  publishable_key: string
}> {
  const res = await api.get('/api/v1/config/stripe-publishable-key')
  return res.data
}

// ── Add-ons ────────────────────────────────────────────────────────────

export interface AvailableAddOn {
  add_on_id: number
  add_on_key: string
  display_name: string
  description: string | null
  billing_model: 'monthly_recurring' | 'annual_recurring' | 'one_time'
  price_cents: number
  currency: string
  tier_required: 'growth' | 'enterprise' | null
}

export interface OrgAddOn {
  org_add_on_id: number
  add_on_id: number
  status: 'active' | 'cancelled' | 'expired'
  billing_model: 'monthly_recurring' | 'annual_recurring' | 'one_time'
  quantity: number
  unit_price_cents: number
  next_billing_date: string | null
  purchased_at: string
  cancelled_at: string | null
  add_on_key: string
  display_name: string
  description: string | null
}

/**
 * List add-ons available for purchase.
 * GET /api/v1/organizations/add-ons/available
 */
export async function getAvailableAddOnsApi(): Promise<{ data: AvailableAddOn[] }> {
  const res = await api.get('/api/v1/organizations/add-ons/available')
  return res.data
}

/**
 * List my org's purchased add-ons.
 * GET /api/v1/organizations/add-ons
 */
export async function getMyAddOnsApi(): Promise<{ data: OrgAddOn[] }> {
  const res = await api.get('/api/v1/organizations/add-ons')
  return res.data
}

/**
 * Purchase an add-on for the org.
 * POST /api/v1/organizations/add-ons/purchase
 */
export async function purchaseAddOnApi(params: {
  add_on_key: string
  quantity?: number
}): Promise<{ message: string; add_on: OrgAddOn }> {
  const res = await api.post('/api/v1/organizations/add-ons/purchase', params)
  return res.data
}

/**
 * Cancel an active add-on.
 * POST /api/v1/organizations/add-ons/:id/cancel
 */
export async function cancelAddOnApi(orgAddOnId: number): Promise<{ message: string }> {
  const res = await api.post(`/api/v1/organizations/add-ons/${orgAddOnId}/cancel`)
  return res.data
}

// ── Admin: Add-on Purchases ────────────────────────────────────────────

export interface AdminOrgAddOn {
  org_add_on_id: number
  add_on_id: number
  org_id: number
  org_name: string
  status: 'active' | 'cancelled' | 'expired'
  quantity: number
  unit_price_cents: number
  next_billing_date: string | null
  purchased_at: string
  cancelled_at: string | null
  add_on_key: string
  display_name: string
  description: string | null
  billing_model: 'monthly_recurring' | 'annual_recurring' | 'one_time'
  tier_required: 'growth' | 'enterprise' | null
}

export interface AdminOrgAddOnsResponse {
  total: number
  page: number
  limit: number
  data: AdminOrgAddOn[]
}

/**
 * List all org add-on purchases (admin).
 * GET /api/v1/admin/add-ons/purchases
 */
export async function getAdminOrgAddOnsApi(params?: {
  org_id?: number
  status?: string
  add_on_key?: string
  page?: number
  limit?: number
}): Promise<AdminOrgAddOnsResponse> {
  const res = await api.get('/api/v1/admin/add-ons/purchases', { params })
  return res.data
}
